import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Megaphone, Phone, Plus, Trash2 } from 'lucide-react';
import { DataTableWithFilters } from '@/components/ui/DataTableWithFilter';
import { Button, ConfirmationDialog, useToast } from '@/components/ui';
import { campaignsService, type Campaign } from '@/services/api/campaigns';
import EditCampaignModal from './modals/EditCampaignModal';
import ScheduleAiCallModal from './modals/ScheduleAiCallModal';
import { DEFAULT_PAGE_SIZE } from '@/helpers';

const STATUS_COLORS: Record<Campaign['status'], string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-600',
  draft: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-blue-100 text-blue-700',
};

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const isCronExpression = (value: string): boolean => {
  const parts = value.trim().split(/\s+/);
  return parts.length === 5;
};

const cronToHuman = (cron: string): string => {
  const [minute, hour, dom, , dow] = cron.trim().split(/\s+/);
  const timeStr = (h: string, m: string) => {
    if (h === '*' || h.startsWith('*/')) return '';
    const hNum = parseInt(h, 10);
    const mNum = parseInt(m, 10);
    const period = hNum < 12 ? 'AM' : 'PM';
    const h12 = hNum % 12 === 0 ? 12 : hNum % 12;
    return ` at ${h12}:${String(mNum).padStart(2, '0')} ${period}`;
  };

  if (hour === '*') return `Every hour at minute ${minute}`;
  if (hour.startsWith('*/')) return `Every ${hour.slice(2)} hours`;

  const t = timeStr(hour, minute);
  if (dom !== '*') return `Monthly on day ${dom}${t}`;
  if (dow === '*') return `Daily${t}`;
  if (dow === '1-5') return `Every weekday (Mon-Fri)${t}`;
  if (dow === '0,6' || dow === '6,0') return `Every weekend (Sat & Sun)${t}`;

  const dayNums = dow.split(',').map(Number).filter((d) => !Number.isNaN(d) && d >= 0 && d <= 6);
  if (dayNums.length === 1) return `Every ${DAYS_FULL[dayNums[0]]}${t}`;
  if (dayNums.length > 1) return `Every ${dayNums.map((d) => DAYS_SHORT[d]).join(', ')}${t}`;
  return cron;
};

const formatScheduledValue = (value: string | null): string => {
  if (!value) return '—';
  if (isCronExpression(value)) return cronToHuman(value);
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return value;
};

const ManageCampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [sortState, setSortState] = useState<{ by?: string; dir?: 'asc' | 'desc' }>({});

  // Modals state
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [schedulingCampaign, setSchedulingCampaign] = useState<Campaign | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);

  const handleSearch = useCallback((q: string) => {
    setSearch(q);
    setCurrentPage(1);
  }, []);

  const handleItemsPerPageChange = useCallback((nextItemsPerPage: number) => {
    setItemsPerPage(nextItemsPerPage);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((col: string, dir: 'asc' | 'desc') => {
    setSortState({ by: col, dir });
    setCurrentPage(1);
  }, []);

  const fetchCampaigns = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await campaignsService.listing({
        page: currentPage,
        page_size: itemsPerPage,
        search: search || undefined,
        sort: sortState.by ? { [sortState.by]: sortState.dir ?? 'desc' } : { created_at: 'desc' },
      });
      const list: Campaign[] =
        res?.attributes?.campaigns ?? res?.attributes?.data ?? res?.data ?? [];
      const total: number = res?.attributes?.pagination?.total ?? list.length;
      setCampaigns(list);
      setTotalItems(total);
    } catch (err: any) {
      toastError(err?.message || 'Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, search, sortState]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const rows = useMemo(
    () =>
      campaigns.map((c) => ({
        id: c.uuid,
        name: c.name,
        description: c.description,
        status: c.status,
        categories: c.lead_categories,
        agents: c.assigned_agent_ids.length,
        scheduledAt: c.scheduled_at,
        createdAt: c.created_at,
        __raw: c,
      })),
    [campaigns],
  );

  const columns = [
    {
      label: 'Campaign Name',
      key: 'name',
      sortable: true,
      render: (v: string) => (
        <span className="font-semibold text-gray-900 flex items-center gap-1.5">
          <Megaphone size={13} className="text-indigo-500 shrink-0" />
          {v}
        </span>
      ),
    },
    {
      label: 'Description',
      key: 'description',
      render: (v: string) => (
        <span className="text-xs text-gray-500 max-w-xs truncate block">{v || '-'}</span>
      ),
    },
    {
      label: 'Status',
      key: 'status',
      render: (v: Campaign['status']) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[v] ?? 'bg-gray-100 text-gray-600'}`}
        >
          {v}
        </span>
      ),
    },
    {
      label: 'Categories',
      key: 'categories',
      render: (v: string[]) => (
        <div className="flex flex-wrap gap-1">
          {v.slice(0, 2).map((cat) => (
            <span
              key={cat}
              className="px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700 font-medium capitalize"
            >
              {cat.replace('_', ' ')}
            </span>
          ))}
          {v.length > 2 && (
            <span className="text-xs text-gray-400">+{v.length - 2}</span>
          )}
        </div>
      ),
    },
    {
      label: 'Agents',
      key: 'agents',
      render: (v: number) => (
        <span className="text-sm font-medium text-gray-700">{v}</span>
      ),
    },
    {
      label: 'Scheduled',
      key: 'scheduledAt',
      render: (v: string | null) => (
        <span className={v ? 'text-xs text-gray-600' : 'text-xs text-gray-400'}>
          {formatScheduledValue(v)}
        </span>
      ),
    },
    {
      label: 'Actions',
      key: 'actions',
      render: (_: any, row: any) => (
        <div className="flex items-center gap-2">
          {/* Edit */}
          <button
            onClick={() => {
              setEditingCampaign(row.__raw);
              setShowEditModal(true);
            }}
            title="Edit Campaign"
            className="p-1.5 rounded hover:bg-indigo-50 transition-colors"
          >
            <Edit size={15} className="text-primary-button" />
          </button>

          {/* Schedule AI Call */}
          <button
            onClick={() => {
              setSchedulingCampaign(row.__raw);
              setShowScheduleModal(true);
            }}
            title="Schedule AI Agent Call"
            className="p-1.5 rounded hover:bg-green-50 transition-colors"
          >
            <Phone size={15} className="text-green-600" />
          </button>

          {/* Delete */}
          <button
            onClick={() => {
              setCampaignToDelete(row.__raw);
              setIsConfirmOpen(true);
            }}
            title="Delete Campaign"
            className="p-1.5 rounded hover:bg-red-50 transition-colors"
          >
            <Trash2 size={15} className="text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  const handleDelete = async () => {
    if (!campaignToDelete) return;
    try {
      await campaignsService.remove(campaignToDelete.uuid);
      success('Campaign deleted successfully');
    } catch (e: any) {
      toastError(e?.message || 'Failed to delete campaign');
    } finally {
      setIsConfirmOpen(false);
      setCampaignToDelete(null);
      fetchCampaigns();
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone size={22} className="text-indigo-600" />
            Manage Campaigns
          </h1>
          <p className="text-xs sm:text-sm text-gray-500">
            View, edit and schedule AI calls for all campaigns.
          </p>
        </div>
        <Button
          onClick={() => navigate('/add-campaign')}
          className="w-full sm:w-auto bg-primary-button hover:bg-primary-button text-white font-bold px-4 py-2 rounded-lg flex justify-center items-center gap-2"
        >
          <Plus size={16} /> Add Campaign
        </Button>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto">
        <DataTableWithFilters
          title="Campaigns"
          columns={columns}
          data={rows}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={handleItemsPerPageChange}
          showFilters={false}
          loading={isLoading}
          onSearch={handleSearch}
          onSort={handleSort}
          searchDebounceMs={500}
        />
      </div>

      {/* Edit Modal */}
      <EditCampaignModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingCampaign(null);
        }}
        campaign={editingCampaign}
        onSuccess={fetchCampaigns}
      />

      {/* Schedule AI Call Modal */}
      <ScheduleAiCallModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setSchedulingCampaign(null);
        }}
        campaign={schedulingCampaign}
      />

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setCampaignToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Campaign"
        message={`Are you sure you want to delete "${campaignToDelete?.name ?? 'this campaign'}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="error"
      />
    </div>
  );
};

export default ManageCampaignsPage;
